import * as React from "react";

import { ChangeEmailDialog } from "@/components/account/ChangeEmailDialog";
import { ChangePasswordSection } from "@/components/account/ChangePasswordSection";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/auth-context";

// Org_admin Settings (Story 14.9, FR84). EXACTLY two sections — change password
// and change assigned email — and nothing else. No mockup exists for this screen
// (UX §13.5 is explicit: "spec-driven — not in prototype"), so the story's W3
// table is the contract and Screen 14 supplies the field patterns.
//
// The absence is as much the requirement as the presence: notification/display
// preferences, org-metadata edits (FR59 — super_admin's surface) and seat
// controls (read-only on the dashboard) are all deliberately NOT here.
//
// The email display reads `user.email` from the auth context, which is a JWT
// claim — so it updates the moment the dialog adopts the re-minted token, with
// no refetch (D2).

export function OrgAdminSettingsScreen() {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [changedTo, setChangedTo] = React.useState<string | null>(null);

  return (
    <section data-testid="org-admin-settings-screen" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="display-xs text-[color:var(--text-primary)]">Settings</h1>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Manage the password and email address you use to sign in to CITS AMPS.
        </p>
      </header>

      <ChangePasswordSection />

      <section className="flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
            Change assigned email
          </h2>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            This is the address you sign in with and where CITS AMPS contacts you.
          </p>
        </div>

        <Card>
          {changedTo ? (
            <Banner variant="success" className="mb-4">
              Your email address has been updated to {changedTo}. A notification
              was sent to your previous address. Other signed-in sessions have
              been signed out.
            </Banner>
          ) : null}

          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-[color:var(--text-tertiary)]">
                Current email
              </span>
              <span
                data-testid="settings-current-email"
                className="text-sm text-[color:var(--text-primary)]"
              >
                {user?.email ?? "—"}
              </span>
            </div>
            <Button variant="secondary" onClick={() => setDialogOpen(true)}>
              Change email
            </Button>
          </div>
        </Card>
      </section>

      {dialogOpen && user?.email ? (
        <ChangeEmailDialog
          currentEmail={user.email}
          onCancel={() => setDialogOpen(false)}
          onChanged={(newEmail) => {
            setDialogOpen(false);
            setChangedTo(newEmail);
          }}
        />
      ) : null}
    </section>
  );
}
